import type { CompanyDocuments } from 'api';
import { EXPORTS_FOLDER_NAME } from 'common/constants/documents';
import { getDocumentReferenceComparator } from 'pages/Documents/utilities/documentReferenceSorting';
import type {
  FirmDocumentFolderInput,
  FirmDocumentTree,
  FirmDocumentTreeFolder,
} from './firmDocumentTree.types';

interface BuildFirmDocumentTreeOptions {
  showEmptyFolders?: boolean;
}

const compareByName = (itemA: { name: string }, itemB: { name: string }) =>
  itemA.name.localeCompare(itemB.name, undefined, { sensitivity: 'base' });

const isExportsFolder = (folder: FirmDocumentTreeFolder) =>
  !folder.isCompanyGroup && folder.name.trim().toLowerCase() === EXPORTS_FOLDER_NAME.toLowerCase();

const compareExportsFolderLast = (
  folderA: FirmDocumentTreeFolder,
  folderB: FirmDocumentTreeFolder
) => {
  const isFolderAExports = isExportsFolder(folderA);
  const isFolderBExports = isExportsFolder(folderB);

  if (isFolderAExports === isFolderBExports) {
    return 0;
  }

  return isFolderAExports ? 1 : -1;
};

const compareFoldersForDisplay = (folderA: FirmDocumentTreeFolder, folderB: FirmDocumentTreeFolder) =>
  compareExportsFolderLast(folderA, folderB) || compareByName(folderA, folderB);

const createFolderNode = (
  folder: FirmDocumentFolderInput,
  defaultMeasurementDateId?: number
): FirmDocumentTreeFolder | null => {
  if (!folder.id) {
    return null;
  }

  return {
    companyId: folder.companyId,
    files: [],
    id: folder.id,
    isCompanyGroup: folder.isCompanyGroup,
    md_documents: folder.md_documents,
    measurementDateId: folder.measurementDateId ?? defaultMeasurementDateId ?? null,
    name: folder.name,
    parent_folder: folder.parent_folder ?? null,
    subFolders: [],
  };
};

const sortFolderNode = (
  folder: FirmDocumentTreeFolder,
  compareDocumentReferences: ReturnType<typeof getDocumentReferenceComparator>,
  compareFolders: typeof compareFoldersForDisplay
): FirmDocumentTreeFolder => ({
  ...folder,
  files: [...folder.files].sort(compareDocumentReferences),
  subFolders: folder.subFolders
    .map(subFolder => sortFolderNode(subFolder, compareDocumentReferences, compareFolders))
    .sort(compareFolders),
});

const pruneEmptyFolderNode = (folder: FirmDocumentTreeFolder): FirmDocumentTreeFolder | null => {
  const subFolders = folder.subFolders
    .map(pruneEmptyFolderNode)
    .filter((subFolder): subFolder is FirmDocumentTreeFolder => Boolean(subFolder));

  if (!folder.files.length && !subFolders.length) {
    return null;
  }

  return {
    ...folder,
    subFolders,
  };
};

const countFolderNodes = (folders: FirmDocumentTreeFolder[]): number =>
  folders.reduce(
    (folderCount, folder) => folderCount + 1 + countFolderNodes(folder.subFolders),
    0
  );

export const buildFirmDocumentTree = (
  documents: CompanyDocuments | null | undefined,
  ordering?: string | null,
  options: BuildFirmDocumentTreeOptions = {}
): FirmDocumentTree => {
  const files = documents?.files ?? [];
  const folders = documents?.folders ?? [];
  const { showEmptyFolders = true } = options;
  const defaultMeasurementDateId = documents?.measurement_date?.id;
  const folderNodesById = new Map<number, FirmDocumentTreeFolder>();
  const compareDocumentReferences = getDocumentReferenceComparator(ordering);

  folders.forEach(folder => {
    const folderNode = createFolderNode(folder, defaultMeasurementDateId);

    if (folderNode) {
      folderNodesById.set(folderNode.id, folderNode);
    }
  });

  const filesWithoutFolder = files.reduce<FirmDocumentTree['filesWithoutFolder']>((accumulator, documentReference) => {
    const folderId = documentReference.folder ?? null;
    const folderNode = folderId ? folderNodesById.get(folderId) : null;

    if (folderNode) {
      folderNode.files.push(documentReference);
      return accumulator;
    }

    accumulator.push(documentReference);
    return accumulator;
  }, []);

  const rootFolders: FirmDocumentTreeFolder[] = [];

  folderNodesById.forEach(folder => {
    const parentFolderId = folder.parent_folder?.id;
    const parentFolder = parentFolderId ? folderNodesById.get(parentFolderId) : null;

    if (parentFolder) {
      parentFolder.subFolders.push(folder);
      return;
    }

    rootFolders.push(folder);
  });

  const sortedFolders = rootFolders
    .map(folder => sortFolderNode(folder, compareDocumentReferences, compareFoldersForDisplay))
    .sort(compareFoldersForDisplay);
  const visibleFolders = showEmptyFolders
    ? sortedFolders
    : sortedFolders
      .map(pruneEmptyFolderNode)
      .filter((folder): folder is FirmDocumentTreeFolder => Boolean(folder));

  return {
    filesWithoutFolder: [...filesWithoutFolder].sort(compareDocumentReferences),
    folders: visibleFolders,
    totalFileCount: files.length,
    totalFolderCount: showEmptyFolders ? folderNodesById.size : countFolderNodes(visibleFolders),
  };
};
